import { EntryType } from '@morph-mapper/types';
import { definePlugin } from '../../framework';
import { getReferenceOptions as getDeclareItemsReferenceOptions } from '../declare-items';
import { getReferenceOptions as getAnchorExtractReferenceOptions } from '../anchor-extract';
import { scoreSuggestion } from '../sort-suggestions';
import { suggestionTypeFor } from '../../utils';

type ReferenceExtraction = {
  referenceLabel: string | null;
  referenceValue: string | null;
};

let cachedReference: ReferenceExtraction | null = null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function parseReferenceResponse(modelText: string): ReferenceExtraction | null {
  try {
    const fenceMatch = modelText.match(/```(?:json)?\s*([\s\S]*?)```/i);
    const cleaned = (fenceMatch ? fenceMatch[1] : modelText).trim();
    const parsed = JSON.parse(cleaned);

    if (!isRecord(parsed)) return null;

    return {
      referenceLabel: typeof parsed['referenceLabel'] === 'string' ? parsed['referenceLabel'].trim() || null : null,
      referenceValue: typeof parsed['referenceValue'] === 'string' ? parsed['referenceValue'].trim() || null : null,
    };
  } catch {
    return null;
  }
}

export function buildSelectorSnippet(referenceLabel: string) {
  const pattern = `${escapeRegExp(referenceLabel)}\\s*:?\\s*([\\w\\-/.]+(?: [\\w\\-/.]+)*)`;

  return {
    clientreference: {
      '$utils.or': [
        {
          '$utils.regexp.match': ['$pdfString', [pattern, 'i']],
          $result: '$data.1',
        },
        { $setVariable: { SKIP: true } },
      ],
    },
  };
}

const MAX_CHARS = 20_000;

const REFERENCE_PROMPT =
  'Identify the client or booking reference number in this document — the value used ' +
  'to uniquely identify this specific order, shipment, or booking.\n' +
  'Return ONLY a JSON object with this exact shape:\n' +
  '{\n' +
  '  "referenceLabel": string | null,\n' +
  '  "referenceValue": string | null\n' +
  '}\n' +
  'referenceLabel is the exact label or heading text printed immediately before the ' +
  'reference value, e.g. "SHIPMENT", "Our reference", "Booking Reference".\n' +
  'referenceValue is the reference value itself, e.g. "MRD2347".\n' +
  'Use null for both if no such reference can be found.\n\n' +
  'DOCUMENT TEXT FOLLOWS:\n';

export const Selector = definePlugin<{}>({
  name: 'Selector',
  version: '0.3.2',
  initialState: {},
  settings: {
    autofill: {
      name: 'Auto-fill suggestions',
      type: 'boolean',
      default: true,
    },
  },

  bootstrap: async ({ textContent, progress, extractWithLLM }) => {
    cachedReference = null;

    if (!textContent || !textContent.trim()) return;

    progress(10, 'Finding client reference');

    const clipped = textContent.length > MAX_CHARS ? textContent.slice(0, MAX_CHARS) : textContent;

    try {
      const response = await extractWithLLM(REFERENCE_PROMPT + clipped, 'Selector_ClientReference');

      if (!response.isOk()) {
        return;
      }

      const modelText = await response.value.text;
      if (!modelText) return;

      cachedReference = parseReferenceResponse(modelText);
      progress(30, 'Client reference found');
    } catch {
      return;
    } finally {
      // 85 is the convention for "this plugin is finished" — the pipeline
      // drives the remaining 15%. In finally so the failure paths above settle
      // the bar too, instead of leaving it frozen at 10%. Everything after
      // bootstrap happens per field, which cannot report progress.
      progress(85, 'Selector ready');
    }
  },

  async fieldSuggestions({ entry, settings, callRender }) {
    if (entry.key !== 'selector') return [];

    const suggestionType = suggestionTypeFor(entry.type);

    const candidates = [
      ...getDeclareItemsReferenceOptions(),
      ...getAnchorExtractReferenceOptions(),
    ];

    if (candidates.length > 0) {
      const scored = await Promise.all(
        candidates.map(async (candidate) => {
          const { score } = await scoreSuggestion(
            {
              value: candidate.value,
              snippet: candidate.snippet as any,
              type: EntryType.Code,
              source: '',
              verified: candidate.verified,
            } as any,
            callRender,
          );
          return { candidate, score };
        }),
      );

      scored.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

      return scored.map(({ candidate }) => ({
        value: candidate.value,
        snippet: { clientreference: candidate.snippet } as any,
        type: suggestionType,
        autofill: Boolean(settings['autofill']),
        verified: candidate.verified,
      }));
    }

    if (!cachedReference?.referenceLabel) return [];

    return [
      {
        value: cachedReference.referenceValue ?? cachedReference.referenceLabel,
        snippet: buildSelectorSnippet(cachedReference.referenceLabel) as any,
        type: suggestionType,
        autofill: Boolean(settings['autofill']),
      },
    ];
  },
});
